import { $fetch } from 'ofetch'
import type { DesignDocument, ProductGeometry } from '@pfe/design-renderer'
import type { ResolvedEditorConfig } from '../config/editorConfig.defaults'

export type DesignDocumentPayload = {
  id: string
  /** Vom Kunden vergebener Name; null = unbenannt. */
  name?: string | null
  version: number
  status: 'draft' | 'ordered' | 'archived'
  side: 'front' | 'back' | null
  linkedDocumentId: string | null
  productId: number
  categoryId: number
  sizeId: number | null
  variantSnapshot: Record<string, any>
  templateId: string | null
  document: DesignDocument
  documentHash: string | null
  renderStatus: string
  previewHash: string | null
  previewUrl: string | null
  thumbnailUrl: string | null
  uploadIds: string[]
  createdAt: string
  updatedAt: string
}

export type DesignApiError = { status: number; error: string; errors?: Array<{ path: string; code: string; message: string }>; current?: DesignDocumentPayload }

function base(editorBaseUrl: string): string {
  return String(editorBaseUrl || '').trim().replace(/\/+$/, '') + '/design'
}

async function call<T>(url: string, opts: any = {}): Promise<T> {
  try {
    return await $fetch<T>(url, { timeout: 30_000, ...opts })
  } catch (e: any) {
    const status = Number(e?.status || e?.response?.status || 0)
    const data = e?.data || {}
    const err: DesignApiError = { status, error: String(data?.error || e?.message || 'request_failed'), errors: data?.errors, current: data?.current }
    throw err
  }
}

export async function createDesign(input: {
  editorBaseUrl: string
  projectCode: string
  productId: number
  categoryId: number
  sizeId?: number | null
  selectedVariantIds?: Array<number | string>
  templateId?: string | null
  document?: DesignDocument | null
  sides?: Array<'front' | 'back'>
}): Promise<{ documents: DesignDocumentPayload[]; geometry: ProductGeometry; config: ResolvedEditorConfig }> {
  return call(`${base(input.editorBaseUrl)}/documents`, {
    method: 'POST',
    body: {
      projectCode: input.projectCode,
      productId: input.productId,
      categoryId: input.categoryId,
      sizeId: input.sizeId ?? null,
      selectedVariantIds: input.selectedVariantIds ?? [],
      templateId: input.templateId ?? null,
      document: input.document ?? null,
      sides: input.sides ?? ['front'],
    },
  })
}

export async function getDesign(input: { editorBaseUrl: string; projectCode: string; id: string }): Promise<{ document: DesignDocumentPayload; geometry: ProductGeometry; config: ResolvedEditorConfig }> {
  return call(`${base(input.editorBaseUrl)}/documents/${encodeURIComponent(input.id)}?projectCode=${encodeURIComponent(input.projectCode)}`)
}

export async function listDesigns(input: { editorBaseUrl: string; projectCode: string; productId?: number | null }): Promise<{ items: DesignDocumentPayload[] }> {
  const q = new URLSearchParams({ projectCode: input.projectCode })
  if (input.productId) q.set('productId', String(input.productId))
  return call(`${base(input.editorBaseUrl)}/documents?${q.toString()}`)
}

export async function saveDesign(input: { editorBaseUrl: string; projectCode: string; id: string; version: number; document: DesignDocument; variantSnapshot?: Record<string, any> | null }): Promise<{ version: number; documentHash: string; previewFresh: boolean; uploadIds: string[] }> {
  return call(`${base(input.editorBaseUrl)}/documents/${encodeURIComponent(input.id)}`, {
    method: 'PUT',
    body: { projectCode: input.projectCode, version: input.version, document: input.document, variantSnapshot: input.variantSnapshot ?? undefined },
  })
}

export async function renameDesign(input: { editorBaseUrl: string; projectCode: string; id: string; name: string | null }): Promise<{ ok: boolean; id: string; name: string | null }> {
  return call(`${base(input.editorBaseUrl)}/documents/${encodeURIComponent(input.id)}`, {
    method: 'PATCH',
    body: { projectCode: input.projectCode, name: input.name },
  })
}

/** Archiviert ein Design (Server: status=archived). Bestellte Designs: 423 document_locked. */
export async function deleteDesign(input: { editorBaseUrl: string; projectCode: string; id: string }): Promise<{ ok: boolean; id: string; status: string }> {
  return call(`${base(input.editorBaseUrl)}/documents/${encodeURIComponent(input.id)}`, {
    method: 'DELETE',
    body: { projectCode: input.projectCode },
  })
}

export async function duplicateDesign(input: { editorBaseUrl: string; projectCode: string; id: string; productId?: number | null; sizeId?: number | null }): Promise<{ document: DesignDocumentPayload }> {
  return call(`${base(input.editorBaseUrl)}/documents/${encodeURIComponent(input.id)}/duplicate`, {
    method: 'POST',
    body: { projectCode: input.projectCode, productId: input.productId ?? undefined, sizeId: input.sizeId ?? undefined },
  })
}

export async function uploadDesignThumbnail(input: { editorBaseUrl: string; projectCode: string; id: string; dataUrl: string }): Promise<{ ok: boolean; thumbnailUrl: string }> {
  return call(`${base(input.editorBaseUrl)}/documents/${encodeURIComponent(input.id)}/thumbnail`, {
    method: 'POST',
    body: { projectCode: input.projectCode, dataUrl: input.dataUrl },
  })
}

export async function requestDesignPreview(input: { editorBaseUrl: string; projectCode: string; id: string }): Promise<{ status: 'done' | 'pending' | 'failed'; previewUrl?: string; renderId?: string; warnings?: any[]; error?: string | null }> {
  return call(`${base(input.editorBaseUrl)}/documents/${encodeURIComponent(input.id)}/preview`, { method: 'POST', body: { projectCode: input.projectCode } })
}

export async function requestDesignProof(input: { editorBaseUrl: string; projectCode: string; id: string }): Promise<{ status: 'done' | 'pending' | 'failed'; proofUrl?: string; proofHash: string; documentHash: string | null; warnings: any[]; error?: string | null }> {
  return call(`${base(input.editorBaseUrl)}/documents/${encodeURIComponent(input.id)}/proof`, { method: 'POST', body: { projectCode: input.projectCode } })
}

export type DesignTemplateSummary = {
  id: string
  version: number
  name: string
  slug: string
  description: string | null
  categoryId: number | null
  tags: string[]
  slotCount: number
  shapes: string[]
  previewUrl: string | null
  previewFilledUrl: string | null
  isPremium: boolean
  surchargeCents: number
  document?: DesignDocument
}

export async function listTemplates(input: { editorBaseUrl: string; productId: number; categoryId?: number | null; sizeId?: number | null; aspect?: number | null }): Promise<{ items: DesignTemplateSummary[]; categories: Array<{ id: number; name: string; slug: string; parentId: number | null }>; aspect: number | null; shape: string | null }> {
  const q = new URLSearchParams({ productId: String(input.productId) })
  if (input.categoryId) q.set('categoryId', String(input.categoryId))
  if (input.sizeId) q.set('sizeId', String(input.sizeId))
  if (input.aspect) q.set('aspect', String(input.aspect))
  return call(`${base(input.editorBaseUrl)}/templates?${q.toString()}`)
}

/** Bögen eines Produkts (editorConfig.sheets) mit Dokument und Zuordnung zu Größen/Optionen */
export async function listSheets(input: { editorBaseUrl: string; productId: number; categoryId?: number | null }): Promise<{ items: DesignTemplateSummary[]; assignments: Array<{ templateId: string; sizeIds: number[]; optionVariantIds: number[]; optionValues: string[]; label: string }>; export: 'slots' | 'sheet' | 'both' }> {
  const q = new URLSearchParams({ productId: String(input.productId) })
  if (input.categoryId) q.set('categoryId', String(input.categoryId))
  return call(`${base(input.editorBaseUrl)}/sheets?${q.toString()}`)
}

export type DesignAssetSummary = {
  id: string
  name: string
  type: string
  categoryId: number | null
  tags: string[]
  url: string | null
  svgUrl: string | null
  pngUrl: string | null
  previewUrl: string | null
  mimeType: string | null
  maskPath: string | null
  isTintable: boolean
  defaultWidthMm: number
  aspectRatio: number
  isPremium: boolean
  surchargeCents: number
}

export async function listAssets(input: { editorBaseUrl: string; productId?: number | null; type?: string | null; categoryId?: number | null; q?: string | null }): Promise<{ items: DesignAssetSummary[]; categories: Array<{ id: number; name: string; slug: string; icon: string | null; type: string | null; parentId: number | null }> }> {
  const q = new URLSearchParams()
  if (input.productId) q.set('productId', String(input.productId))
  if (input.type) q.set('type', input.type)
  if (input.categoryId) q.set('categoryId', String(input.categoryId))
  if (input.q) q.set('q', input.q)
  return call(`${base(input.editorBaseUrl)}/assets?${q.toString()}`)
}

export type DesignFontSummary = {
  id: string
  name: string
  family: string
  slug: string
  category: string
  previewUrl: string | null
  faces: Array<{ id: number; weight: number; italic: boolean; woff2Url: string; ttfUrl: string | null; hash: string | null }>
}

export async function listFonts(input: { editorBaseUrl: string; productId?: number | null; categoryId?: number | null }): Promise<{ items: DesignFontSummary[] }> {
  const q = new URLSearchParams()
  if (input.productId) q.set('productId', String(input.productId))
  if (input.categoryId) q.set('categoryId', String(input.categoryId))
  return call(`${base(input.editorBaseUrl)}/fonts?${q.toString()}`)
}
